import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { MapPin, Users, ArrowRight, ArrowDown, Shield, CreditCard, Waves, Wifi, Tv, Flame, Heart, UtensilsCrossed, Dumbbell, Music, Baby, Sun, BedDouble, Bath, Car, CheckCircle, Star, MessageCircle, Lock, Clock, Ban, PawPrint, Phone, ShowerHead, Armchair, Loader2, Send, AlertCircle } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';

/* ─── REAL CARAVAN HERO IMAGE ─── */
const HERO_IMG = "https://customer-assets.emergentagent.com/job_nf-caravan-book/artifacts/uppatbkt_IMG_8495.jpeg";

/* ─── STATIC DATA ─── */
const CARAVAN_LABELS = [
  { label: "Living Area", alt: "Spacious living and dining area inside our Hoburne Park caravan" },
  { label: "Kitchen", alt: "Fully equipped modern kitchen in our New Forest holiday caravan" },
  { label: "Master Bedroom", alt: "Comfortable master bedroom with double bed at Hoburne Park caravan" },
  { label: "Dining Area", alt: "Dining space with seating for the family in our caravan at Hoburne Park" },
  { label: "Open Plan Living", alt: "Open-plan living space with natural light in our New Forest caravan" },
  { label: "Twin Room", alt: "Twin bedroom with two single beds — perfect for children or adults" },
  { label: "Kitchen Detail", alt: "Modern kitchen appliances and worktop in our Hoburne Park holiday caravan" },
  { label: "Exterior & Decking", alt: "Caravan exterior with private decking at Hoburne Park New Forest" },
  { label: "Park Setting", alt: "Woodland surroundings at Hoburne Park — a New Forest caravan holiday destination" },
];

const FEATURES = [
  { icon: UtensilsCrossed, label: "Fully Equipped Kitchen" },
  { icon: Wifi, label: "Free WiFi" },
  { icon: Tv, label: "Smart TV" },
  { icon: Flame, label: "Central Heating" },
  { icon: Armchair, label: "Outdoor Seating" },
  { icon: Heart, label: "Family Friendly" },
  { icon: Waves, label: "Close to Beach" },
  { icon: Car, label: "Free Parking" },
  { icon: BedDouble, label: "Linen & Towels" },
  { icon: ShowerHead, label: "Modern Bathroom" },
];

const SLEEPING = [
  { room: "Bedroom 1", desc: "Double bed", detail: "Spacious master with wardrobe storage", img: "https://customer-assets.emergentagent.com/job_nf-caravan-book/artifacts/aegncg9v_IMG_8518.jpeg", alt: "Master bedroom with double bed in our Hoburne Park holiday caravan" },
  { room: "Bedroom 2", desc: "Twin beds", detail: "Two single beds, great for kids or adults", img: "https://customer-assets.emergentagent.com/job_nf-caravan-book/artifacts/obgaxba3_IMG_8521.jpeg", alt: "Twin bedroom with two single beds at Hoburne Park caravan" },
  { room: "Bedroom 3", desc: "Twin / bunk beds", detail: "Flexible space, ideal for children", img: "https://customer-assets.emergentagent.com/job_nf-caravan-book/artifacts/obgaxba3_IMG_8521.jpeg", alt: "Third bedroom with twin beds — ideal for children at our New Forest caravan" },
];

const FACILITIES = [
  { icon: Waves, label: "Indoor & Outdoor Pools" },
  { icon: UtensilsCrossed, label: "Restaurant & Bar" },
  { icon: Music, label: "Entertainment" },
  { icon: Baby, label: "Kids Activities" },
  { icon: Dumbbell, label: "Gym & Leisure" },
  { icon: Car, label: "Shop & Amenities" },
];

const PARK_GALLERY = [
  { url: "https://images.unsplash.com/photo-1763051338904-e2434c7dd20a?w=600&q=80", label: "Pool Complex", alt: "Indoor swimming pool complex at Hoburne Park holiday park in the New Forest" },
  { url: "https://images.unsplash.com/photo-1772146775507-4df2400b9efd?w=600&q=80", label: "Park Grounds", alt: "Well-maintained park grounds and accommodation at Hoburne Park Christchurch" },
  { url: "https://images.unsplash.com/photo-1609094582122-43a973dd2667?w=600&q=80", label: "Woodland Setting", alt: "Woodland setting surrounding Hoburne Park near the New Forest National Park" },
  { url: "https://images.unsplash.com/photo-1638202948587-ac48463ddb1f?w=600&q=80", label: "Family Activities", alt: "Family enjoying activities at Hoburne Park caravan holiday destination" },
  { url: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&q=80", label: "Nearby Beach", alt: "Sandy beach near Hoburne Park — Avon Beach is just 5 minutes away" },
  { url: "https://images.unsplash.com/photo-1686931346464-5cf66d798b47?w=600&q=80", label: "Park Surroundings", alt: "Peaceful surroundings and green spaces at Hoburne Park Dorset" },
  { url: "https://images.unsplash.com/photo-1759805583291-704d7af07682?w=600&q=80", label: "Poolside Leisure", alt: "Relaxing poolside loungers at Hoburne Park holiday resort" },
  { url: "https://images.unsplash.com/photo-1759352506768-414190a88146?w=600&q=80", label: "Restaurant & Bar", alt: "Evening dining and restaurant facilities at Hoburne Park" },
  { url: "https://images.unsplash.com/photo-1759744869584-4707bf349525?w=600&q=80", label: "Outdoor Dining", alt: "Outdoor dining area at Hoburne Park perfect for family meals" },
];

const NEARBY = [
  { name: "Avon Beach", distance: "5 min drive", desc: "Sandy family beach with calm waters, colourful beach huts and a popular beachside cafe.", img: "https://images.unsplash.com/photo-1642607735693-198e2c25e697?w=400&q=80", cat: "Beach" },
  { name: "Mudeford Quay", distance: "8 min drive", desc: "Famous fishing quay with ferry to Mudeford Sandbank. Fresh seafood, crabbing, and harbour views.", img: "https://images.unsplash.com/photo-1659654971675-9d392d8227e6?w=400&q=80", cat: "Beach" },
  { name: "Christchurch Harbour", distance: "10 min drive", desc: "Historic harbour with a stunning 900-year-old Priory, riverside walks, and independent shops.", img: "https://images.unsplash.com/photo-1645471124039-324819acd228?w=400&q=80", cat: "Town" },
  { name: "New Forest National Park", distance: "Adjacent", desc: "Ancient woodland, wild ponies, and endless walking and cycling trails right on your doorstep.", img: "https://images.unsplash.com/photo-1609094582122-43a973dd2667?w=400&q=80", cat: "Nature" },
  { name: "Bournemouth Beach", distance: "20 min drive", desc: "Award-winning 7-mile golden sand with vibrant pier, water sports, and restaurants.", img: "https://images.unsplash.com/photo-1629231736011-e68ad4835083?w=400&q=80", cat: "Beach" },
  { name: "Local Pubs & Restaurants", distance: "5-15 min", desc: "Cosy New Forest pubs, seafood on Mudeford Quay, and family-friendly dining close by.", img: "https://images.unsplash.com/photo-1552566626-52f8b828add9?w=400&q=80", cat: "Dining" },
];

const REVIEWS = [
  { name: "Sarah T.", date: "March 2026", rating: 5, text: "Beautifully clean and perfect location — we'll definitely return. The kids loved the pool and we could walk to the beach. Highly recommend booking direct!" },
  { name: "James & Emma", date: "February 2026", rating: 5, text: "Fantastic caravan, very well equipped. Sue was incredibly helpful with local tips. Much better value than booking through Airbnb." },
  { name: "The Williams Family", date: "January 2026", rating: 5, text: "Our second stay and it won't be our last. The caravan is spotless and the location is unbeatable for family holidays." },
  { name: "Mark D.", date: "December 2025", rating: 4, text: "Great caravan in a lovely park. Very comfortable for our family of 5. The New Forest walks nearby were a real highlight." },
  { name: "Lucy & Tom", date: "November 2025", rating: 5, text: "We came for a week and didn't want to leave. The caravan had everything we needed. Booking direct saved us a fortune." },
];

const HOUSE_RULES = [
  { icon: Clock, text: "Check-in: 3:00 PM" },
  { icon: Clock, text: "Check-out: 10:00 AM" },
  { icon: Ban, text: "No smoking" },
  { icon: Ban, text: "No parties or events" },
  { icon: PawPrint, text: "Pets: Please enquire" },
  { icon: Shield, text: "Security deposit: £250 (refundable)" },
];

/* ─── COMPONENT ─── */
export default function HomePage() {
  const [caravan, setCaravan] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [priceInfo, setPriceInfo] = useState(null);
  const [availability, setAvailability] = useState(null); // null=not checked, true=available, false=unavailable
  const [checkingAvail, setCheckingAvail] = useState(false);
  const navigate = useNavigate();

  // Enquiry form state
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [enquiry, setEnquiry] = useState({ name: '', email: '', phone: '', message: '' });
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);
  const [enquiryResult, setEnquiryResult] = useState(null);

  useEffect(() => {
    api.get('/caravans/woodland-lodge-hoburne-park').then(r => setCaravan(r.data)).catch(() => {});
    api.get('/caravans/woodland-lodge-hoburne-park/availability').then(r => {
      const all = [...(r.data.booked_dates || []), ...(r.data.blocked_dates || [])];
      setBookedDates(all.map(d => new Date(d)));
    }).catch(() => {});
  }, []);

  // Check price + availability when dates change
  useEffect(() => {
    if (dateRange.from && dateRange.to && caravan) {
      const ci = dateRange.from.toISOString().split('T')[0];
      const co = dateRange.to.toISOString().split('T')[0];
      setCheckingAvail(true);
      setAvailability(null);
      setShowEnquiry(false);
      setEnquiryResult(null);
      Promise.all([
        api.get(`/pricing/calculate?caravan_id=${caravan.id}&check_in=${ci}&check_out=${co}`),
        api.post(`/bookings/check-availability?caravan_id=${caravan.id}&check_in=${ci}&check_out=${co}`)
      ]).then(([priceRes, availRes]) => {
        setPriceInfo(priceRes.data);
        setAvailability(availRes.data.available);
        if (!availRes.data.available) setShowEnquiry(true);
        setCheckingAvail(false);
      }).catch(() => { setCheckingAvail(false); setPriceInfo(null); });
    } else {
      setPriceInfo(null);
      setAvailability(null);
      setShowEnquiry(false);
    }
  }, [dateRange, caravan]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const handleBook = () => {
    if (!dateRange.from || !dateRange.to) { scrollTo('availability'); return; }
    navigate(`/book/woodland-lodge-hoburne-park?checkin=${dateRange.from.toISOString().split('T')[0]}&checkout=${dateRange.to.toISOString().split('T')[0]}`);
  };

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setEnquirySubmitting(true);
    try {
      await api.post('/enquiries', {
        ...enquiry,
        arrival_date: dateRange.from?.toISOString().split('T')[0] || '',
        departure_date: dateRange.to?.toISOString().split('T')[0] || '',
        num_guests: 1,
      });
      setEnquiryResult('success');
    } catch (err) {
      setEnquiryResult('error');
    }
    setEnquirySubmitting(false);
  };

  const imgs = caravan?.images || [];
  const avgRating = (REVIEWS.reduce((s, r) => s + r.rating, 0) / REVIEWS.length).toFixed(1);

  // Lightbox state
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const openLightbox = (images, idx) => { setLightboxImages(images); setLightboxIdx(idx); };
  const closeLightbox = () => { setLightboxIdx(null); setLightboxImages([]); };

  return (
    <div data-testid="home-page">

      {/* ── LIGHTBOX MODAL ── */}
      {lightboxIdx !== null && lightboxImages.length > 0 && (
        <div className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center" onClick={closeLightbox} data-testid="lightbox-modal">
          <button onClick={closeLightbox} className="absolute top-4 right-4 text-white/80 hover:text-white z-10 text-3xl font-light" aria-label="Close">&times;</button>
          <button onClick={(e) => { e.stopPropagation(); setLightboxIdx(Math.max(0, lightboxIdx - 1)); }} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-10 bg-black/30 rounded-full w-10 h-10 flex items-center justify-center" aria-label="Previous">&lsaquo;</button>
          <button onClick={(e) => { e.stopPropagation(); setLightboxIdx(Math.min(lightboxImages.length - 1, lightboxIdx + 1)); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-10 bg-black/30 rounded-full w-10 h-10 flex items-center justify-center" aria-label="Next">&rsaquo;</button>
          <div className="max-w-[90vw] max-h-[85vh] flex flex-col items-center" onClick={e => e.stopPropagation()}>
            <img src={lightboxImages[lightboxIdx]?.url} alt={lightboxImages[lightboxIdx]?.alt || ''} className="max-w-full max-h-[75vh] object-contain rounded" />
            <p className="text-sm text-white/80 mt-3 text-center">{lightboxImages[lightboxIdx]?.label || lightboxImages[lightboxIdx]?.alt}</p>
            <p className="text-xs text-white/40 mt-1">{lightboxIdx + 1} / {lightboxImages.length}</p>
          </div>
        </div>
      )}

      {/* ── 1. HERO (Caravan-focused) ── */}
      <section className="hero-section flex items-center justify-center min-h-[90vh]" data-testid="hero-section">
        <img src={HERO_IMG} alt="Luxury holiday caravan with private decking at Hoburne Park in the New Forest, Dorset" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 z-[1]" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.50) 0%, rgba(0,0,0,0.60) 50%, rgba(0,0,0,0.75) 100%)' }} />
        <div className="relative z-[2] text-center px-6 py-24 max-w-[700px] mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#CC5A3D] mb-6 animate-fade-in-up">Direct Booking &middot; No Platform Fees</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] font-semibold text-white mb-5 animate-fade-in-up delay-100" data-testid="hero-h1" style={{ textShadow: '0 2px 16px rgba(0,0,0,0.6)' }}>
            Luxury Caravan at Hoburne&nbsp;Park, New&nbsp;Forest
          </h1>
          <p className="text-base md:text-lg text-white/95 mb-6 leading-relaxed animate-fade-in-up delay-200" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}>
            Premium family caravan holidays near Christchurch. Book direct and save on fees.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 mb-8 animate-fade-in-up delay-200">
            <span className="flex items-center gap-2 text-sm text-white/90" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}><CheckCircle className="w-4 h-4 text-[#CC5A3D]" /> Sleeps up to 6 guests</span>
            <span className="flex items-center gap-2 text-sm text-white/90" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}><CheckCircle className="w-4 h-4 text-[#CC5A3D]" /> 5 minutes from Avon Beach</span>
            <span className="flex items-center gap-2 text-sm text-white/90" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}><CheckCircle className="w-4 h-4 text-[#CC5A3D]" /> No Airbnb fees — book direct</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-300">
            <Button onClick={() => scrollTo('availability')} className="bg-[#CC5A3D] text-white hover:bg-[#B34A30] rounded-sm px-10 py-3.5 text-base font-medium shadow-lg shadow-black/20" data-testid="hero-cta-prices">
              Check Availability <ArrowDown className="w-4 h-4 ml-2" />
            </Button>
            <Button onClick={() => scrollTo('caravan')} variant="outline" className="border-white/80 text-white hover:bg-white/10 rounded-sm px-8 py-3.5 text-base bg-transparent" data-testid="hero-cta-caravan">
              View Caravan
            </Button>
          </div>
        </div>
      </section>

      {/* ── SEO INTRODUCTION ── */}
      <section className="pt-14 pb-8 md:pt-16 md:pb-10 px-6 md:px-12 lg:px-24" data-testid="seo-intro-section">
        <div className="max-w-[780px] mx-auto">
          <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-[#1F2A25] mb-8 text-center">Hoburne Park Caravan Holidays in the New Forest</h2>
          <div className="space-y-5 text-[15px] leading-[1.75] text-[#6B726A]">
            <p>
              Looking for <strong className="text-[#1F2A25] font-medium">Hoburne Park caravan hire</strong>? Our privately owned holiday caravan offers a comfortable, high-quality alternative to hotel stays and traditional <strong className="text-[#1F2A25] font-medium">Hoburne Park accommodation</strong>. Located near Christchurch on the beautiful Dorset coast, you'll be just five minutes from Avon Beach and Mudeford Quay — with the ancient <strong className="text-[#1F2A25] font-medium">New Forest</strong> National Park right on your doorstep.
            </p>
            <p>
              Whether you're planning a weekend break or a full week away, our caravan is the ideal base for <strong className="text-[#1F2A25] font-medium">Dorset family caravan holidays</strong>. Explore miles of sandy beaches, cycle through the New Forest, visit Paultons Park with the kids, or simply relax at the park's heated pools and entertainment complex. With three bedrooms sleeping up to six guests, there's plenty of space for families and small groups alike.
            </p>
            <p>
              When you <strong className="text-[#1F2A25] font-medium">book direct</strong>, you avoid the service fees charged by Airbnb and other platforms — meaning <strong className="text-[#1F2A25] font-medium">no booking fees</strong> and better value for your holiday. You'll also benefit from direct communication with your hosts, and local management from Sue who lives nearby and is always on hand to help. For <strong className="text-[#1F2A25] font-medium">caravan hire near Christchurch</strong>, you won't find a more personal or straightforward booking experience.
            </p>
            <p>
              Hoburne Park itself is one of the most popular holiday parks on the south coast, offering indoor and outdoor pools, live entertainment, a restaurant and bar, children's activities, and beautifully maintained grounds. It's easy to see why families return year after year for their <strong className="text-[#1F2A25] font-medium">New Forest caravan holidays</strong>.
            </p>
          </div>
          <div className="mt-8 text-center">
            <Button onClick={() => scrollTo('availability')} className="bg-[#CC5A3D] text-white hover:bg-[#B34A30] rounded-sm px-8 py-2.5 text-sm" data-testid="seo-intro-cta">
              Check Availability <ArrowDown className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ── 2. YOUR CARAVAN (Primary selling point - FIRST) ── */}
      <section id="caravan" className="py-16 px-6 md:px-12 lg:px-24" data-testid="caravan-section">
        <div className="max-w-[1440px] mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#CC5A3D] mb-3">Your Holiday Home</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#1F2A25] mb-2">Your Caravan at Hoburne Park</h2>
          <p className="text-sm text-[#6B726A] mb-6">Privately owned &middot; Managed locally by Sue &middot; Direct booking</p>

          {/* Trust intro text */}
          <div className="max-w-2xl mb-8 space-y-2">
            <p className="text-base text-[#6B726A] leading-relaxed">
              A <strong className="text-[#1F2A25] font-medium">privately owned caravan</strong>, personally managed by Sue who lives locally. No agency, no platform fees — just a genuine, high-quality holiday home that's better value than Airbnb.
            </p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-1">
              <span className="flex items-center gap-1.5 text-xs text-[#1E3F33] font-medium"><CheckCircle className="w-3.5 h-3.5 text-[#2D6A4F]" /> Direct booking — no hidden fees</span>
              <span className="flex items-center gap-1.5 text-xs text-[#1E3F33] font-medium"><CheckCircle className="w-3.5 h-3.5 text-[#2D6A4F]" /> Managed locally for personal service</span>
              <span className="flex items-center gap-1.5 text-xs text-[#1E3F33] font-medium"><Star className="w-3.5 h-3.5 text-[#CC5A3D]" fill="#CC5A3D" /> {avgRating}/5 from {REVIEWS.length} guests</span>
            </div>
          </div>

          {/* Key Info Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="caravan-key-details">
            {[
              { icon: Users, label: `Sleeps ${caravan?.sleeps || 6}`, sub: "guests" },
              { icon: BedDouble, label: `${caravan?.bedrooms || 3} Bedrooms` },
              { icon: Bath, label: `${caravan?.bathrooms || 1} Bathroom` },
              { icon: Car, label: "Parking Available" },
            ].map((d, i) => (
              <div key={i} className="bg-[#F0EBE1] border border-[#E6D5B8] p-4 rounded-lg text-center">
                <d.icon className="w-5 h-5 text-[#1E3F33] mx-auto mb-2" />
                <span className="text-sm font-medium text-[#1F2A25] block">{d.label}</span>
              </div>
            ))}
          </div>

          {/* Host Info */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-10 bg-white border border-[#E6D5B8] p-5 rounded-lg" data-testid="host-info">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#1E3F33] rounded-full flex items-center justify-center text-white font-heading text-lg">N</div>
              <div>
                <p className="text-sm font-medium text-[#1F2A25]">Hosted by <strong>Nick</strong></p>
                <p className="text-xs text-[#6B726A]">Managed locally by <strong className="text-[#1E3F33]">Sue</strong></p>
              </div>
            </div>
            <Separator orientation="vertical" className="h-8 hidden sm:block" />
            <div className="flex items-center gap-2 text-sm text-[#6B726A]">
              <Phone className="w-4 h-4 text-[#CC5A3D]" />
              <span>Local support during your stay</span>
            </div>
            <div className="flex items-center gap-1 ml-auto">
              <Star className="w-4 h-4 text-[#CC5A3D]" fill="#CC5A3D" />
              <span className="text-sm font-medium text-[#1F2A25]">{avgRating}</span>
              <span className="text-xs text-[#6B726A]">({REVIEWS.length} reviews)</span>
            </div>
          </div>

          {/* Caravan Gallery — Horizontal scroll with lightbox */}
          <div className="mb-10" data-testid="caravan-gallery">
            <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-thin" style={{ scrollbarWidth: 'thin' }}>
              {imgs.slice(0, 9).map((img, i) => {
                const meta = CARAVAN_LABELS[i] || { label: img.category, alt: img.alt };
                const lightboxData = imgs.slice(0, 9).map((im, j) => ({ url: im.url, alt: (CARAVAN_LABELS[j] || {}).alt || im.alt, label: (CARAVAN_LABELS[j] || {}).label || im.category }));
                return (
                  <div key={i} className="snap-start shrink-0 w-[280px] sm:w-[320px] md:w-[360px] rounded-lg overflow-hidden border border-[#E6D5B8] cursor-pointer transition-transform hover:-translate-y-1" onClick={() => openLightbox(lightboxData, i)} data-testid={`caravan-img-${i}`}>
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={img.url} alt={meta.alt || img.alt} title={meta.label} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" loading="lazy" />
                    </div>
                    <div className="bg-[#F9F8F6] px-4 py-2.5">
                      <span className="text-xs font-medium text-[#1E3F33]">{meta.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-[#6B726A] mt-2 text-center">Scroll to see more &middot; Click any image to enlarge</p>
          </div>

          {/* Features + Description side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7 space-y-8">
              <div data-testid="caravan-description">
                <h3 className="text-xl font-heading font-medium text-[#1F2A25] mb-3">About This Caravan</h3>
                <p className="text-base leading-relaxed text-[#6B726A] mb-3">
                  <strong className="text-[#1F2A25] font-medium">Perfect for families</strong> looking for a comfortable, modern base to explore the New Forest and Dorset coast. This recently refurbished 3-bedroom caravan features a <strong className="text-[#1F2A25] font-medium">spacious open-plan living area</strong>, fully equipped kitchen, and private decking — everything you need for a relaxing break.
                </p>
                <p className="text-base leading-relaxed text-[#6B726A] mb-3">
                  Wake up to woodland views, spend the day at the beach or exploring the New Forest, then come back to a <strong className="text-[#1F2A25] font-medium">modern and comfortable</strong> home from home. The kids can enjoy the park's pools and entertainment while you unwind.
                </p>
                <p className="text-base leading-relaxed text-[#6B726A]">
                  An <strong className="text-[#1F2A25] font-medium">ideal base for exploring the New Forest</strong>, Christchurch, and the Jurassic Coast — with the convenience of a premium holiday park on your doorstep.
                </p>
              </div>
              <div data-testid="caravan-features">
                <h3 className="text-xl font-heading font-medium text-[#1F2A25] mb-4">Features & Amenities</h3>
                <div className="grid grid-cols-2 gap-3">
                  {FEATURES.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-[#1F2A25]">
                      <f.icon className="w-4 h-4 text-[#2D6A4F] flex-shrink-0" /> {f.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-white border border-[#E6D5B8] p-6 rounded-xl shadow-[0_8px_30px_rgba(30,63,51,0.04)] sticky top-24" data-testid="pricing-card">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-2xl font-heading font-medium text-[#1F2A25]">From &pound;{caravan?.pricing?.off_peak || 85}</span>
                  <span className="text-sm text-[#6B726A]">/ night</span>
                </div>
                <div className="bg-[#2D6A4F] text-white text-xs font-medium px-3 py-1.5 rounded mb-3 inline-block">No booking fees</div>
                <p className="text-xs text-[#6B726A] mb-4">Best rates when booking direct &middot; No Airbnb or platform fees</p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-[#F0EBE1] p-3 rounded text-center">
                    <p className="text-[10px] uppercase tracking-wider text-[#6B726A]">Off-Peak</p>
                    <p className="text-lg font-heading font-medium text-[#1E3F33]">&pound;{caravan?.pricing?.off_peak || 85}</p>
                  </div>
                  <div className="bg-[#F0EBE1] p-3 rounded text-center">
                    <p className="text-[10px] uppercase tracking-wider text-[#6B726A]">Peak</p>
                    <p className="text-lg font-heading font-medium text-[#CC5A3D]">&pound;{caravan?.pricing?.peak || 135}</p>
                  </div>
                </div>
                <Button onClick={() => scrollTo('availability')} className="w-full bg-[#CC5A3D] text-white hover:bg-[#B34A30] rounded-sm py-3" data-testid="pricing-cta">
                  Check Availability <ArrowDown className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          {/* Sleeping Arrangements */}
          <div className="mt-12" data-testid="sleeping-section">
            <h3 className="text-xl font-heading font-medium text-[#1F2A25] mb-6">Sleeping Arrangements</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {SLEEPING.map((s, i) => (
                <div key={i} className="bg-white border border-[#E6D5B8] rounded-lg overflow-hidden" data-testid={`bedroom-${i}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={s.img} alt={s.alt} title={s.room} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-5 text-center">
                    <h4 className="text-lg font-heading font-medium text-[#1F2A25] mb-1">{s.room}</h4>
                    <p className="text-sm font-medium text-[#CC5A3D] mb-1">{s.desc}</p>
                    <p className="text-xs text-[#6B726A]">{s.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST SIGNALS ── */}
      <section className="py-14 px-6 md:px-12 lg:px-24 bg-white border-y border-[#E6D5B8]" data-testid="trust-signals-section">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-1 mb-2">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 text-[#CC5A3D]" fill="#CC5A3D" />)}
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-3xl font-heading font-medium text-[#1F2A25]">{avgRating} / 5</span>
          </div>
          <p className="text-base font-medium text-[#1E3F33] mb-1">Guest Favourite</p>
          <p className="text-sm text-[#6B726A] mb-6">Highly rated for cleanliness, comfort and location</p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {[
              { icon: CheckCircle, text: "Managed locally by Sue" },
              { icon: CheckCircle, text: "Direct booking — no hidden fees" },
              { icon: CheckCircle, text: "Better value than Airbnb" },
            ].map((item, i) => (
              <span key={i} className="flex items-center gap-2 text-sm text-[#1E3F33] font-medium">
                <item.icon className="w-4 h-4 text-[#2D6A4F]" /> {item.text}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY BOOK DIRECT — PROMINENT SECTION ── */}
      <section className="py-14 px-6 md:px-12 lg:px-24 bg-[#1E3F33]" data-testid="book-direct-strip">
        <div className="max-w-[1440px] mx-auto">
          <h3 className="text-xl sm:text-2xl font-heading font-medium text-white text-center mb-8">Why Book Direct With Us</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: CreditCard, title: "No Booking Fees", sub: "Save up to 15% vs Airbnb" },
              { icon: Shield, title: "Better Value", sub: "Best price guaranteed, always" },
              { icon: MessageCircle, title: "Direct Communication", sub: "Speak to Nick & Sue directly" },
              { icon: MapPin, title: "Local Management", sub: "Sue is on hand during your stay" },
            ].map((item, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-lg p-5 text-center" data-testid={`book-direct-card-${i}`}>
                <div className="w-10 h-10 bg-[#CC5A3D] rounded-full flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-sm font-medium text-white mb-1">{item.title}</p>
                <p className="text-xs text-white/60">{item.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. AVAILABILITY & BOOKING + ENQUIRY SYSTEM ── */}
      <section id="availability" className="py-16 px-6 md:px-12 lg:px-24 bg-[#F0EBE1]" data-testid="availability-section">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#CC5A3D] mb-3 text-center">Book Your Stay</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#1F2A25] mb-2 text-center">Check Availability & Secure Your Stay</h2>
          <p className="text-sm text-[#6B726A] text-center mb-2">From &pound;{caravan?.pricing?.off_peak || 85} per night &middot; No booking fees</p>
          <p className="text-xs text-[#CC5A3D] text-center mb-8 font-medium">Popular summer dates fill quickly — enquire early to avoid missing out</p>

          <div className="bg-white border border-[#E6D5B8] p-6 md:p-8 rounded-xl shadow-[0_8px_30px_rgba(30,63,51,0.04)]">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={setDateRange}
              numberOfMonths={2}
              disabled={[{ before: new Date() }]}
              modifiers={{
                arrivalDay: (date) => {
                  const day = date.getDay();
                  return (day === 1 || day === 5) && date >= new Date(new Date().toDateString());
                },
                booked: bookedDates,
              }}
              modifiersClassNames={{
                arrivalDay: 'arrival-day',
                booked: 'booked-day',
              }}
              className="mx-auto mb-4"
              data-testid="booking-calendar"
            />

            {/* Arrival day legend */}
            <div className="mb-6 space-y-3" data-testid="arrival-legend">
              <div className="flex items-center gap-4 justify-center flex-wrap text-xs text-[#6B726A]">
                <span className="flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-full border-2 border-[#1E3F33] inline-flex items-center justify-center text-[9px] font-medium text-[#1E3F33]">A</span>
                  Arrival day
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-5 h-1 bg-[#DC2626] rounded inline-block relative top-px" />
                  Unavailable
                </span>
              </div>
              <div className="bg-[#F0EBE1] border border-[#E6D5B8] rounded-lg p-4" data-testid="stay-options">
                <p className="text-xs font-medium text-[#1F2A25] mb-2 text-center">Arrival days &amp; stay options</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  <div className="bg-white rounded p-2.5 border border-[#E6D5B8]">
                    <p className="text-xs font-medium text-[#1E3F33]">Mon → Fri</p>
                    <p className="text-[11px] text-[#6B726A]">4 nights</p>
                  </div>
                  <div className="bg-white rounded p-2.5 border border-[#E6D5B8]">
                    <p className="text-xs font-medium text-[#1E3F33]">Fri → Mon</p>
                    <p className="text-[11px] text-[#6B726A]">3 nights</p>
                  </div>
                  <div className="bg-white rounded p-2.5 border border-[#E6D5B8]">
                    <p className="text-xs font-medium text-[#1E3F33]">Mon → Mon</p>
                    <p className="text-[11px] text-[#6B726A]">7 nights</p>
                  </div>
                  <div className="bg-white rounded p-2.5 border border-[#E6D5B8]">
                    <p className="text-xs font-medium text-[#1E3F33]">Fri → Fri</p>
                    <p className="text-[11px] text-[#6B726A]">7 nights</p>
                  </div>
                </div>
              </div>
            </div>

            {checkingAvail && (
              <div className="text-center py-4"><Loader2 className="w-6 h-6 animate-spin text-[#CC5A3D] mx-auto" /><p className="text-sm text-[#6B726A] mt-2">Checking availability...</p></div>
            )}

            {/* AVAILABLE — show price + Book Now */}
            {availability === true && priceInfo && (
              <div data-testid="dates-available">
                <div className="bg-[#F0EBE1] border border-[#E6D5B8] p-5 rounded-lg mb-6" data-testid="price-display">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#6B726A]">{priceInfo.nights} night{priceInfo.nights > 1 ? 's' : ''}</span>
                    <span className="font-medium text-[#1F2A25]">&pound;{priceInfo.total_price.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-[#6B726A]">Booking fees</span>
                    <span className="font-medium text-[#2D6A4F]">&pound;0.00</span>
                  </div>
                  <div className="border-t border-[#E6D5B8] pt-2 mt-2 flex justify-between">
                    <span className="font-medium text-[#1F2A25]">Total</span>
                    <span className="text-2xl font-heading font-medium text-[#CC5A3D]">&pound;{priceInfo.total_price.toFixed(2)}</span>
                  </div>
                </div>
                <Button onClick={handleBook} className="w-full bg-[#CC5A3D] text-white hover:bg-[#B34A30] rounded-sm py-3 text-base" data-testid="book-now-btn">
                  Book Now — &pound;{priceInfo.total_price.toFixed(2)} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-xs text-center text-[#6B726A] mt-3 flex items-center justify-center gap-1">
                  <Lock className="w-3 h-3" /> Secure payment &middot; No Airbnb fees &middot; Best price guaranteed
                </p>
              </div>
            )}

            {/* UNAVAILABLE — show enquiry form */}
            {availability === false && !enquiryResult && (
              <div data-testid="dates-unavailable">
                <div className="bg-amber-50 border border-amber-200 p-5 rounded-lg mb-6 text-center">
                  <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                  <p className="text-base font-medium text-[#1F2A25] mb-2">Sorry, these dates are currently unavailable.</p>
                  <p className="text-sm text-[#6B726A] mb-1">But don't worry — we may still be able to help.</p>
                  <p className="text-sm text-[#6B726A]">We work closely with other trusted caravan owners at Hoburne Park and can help you find a similar holiday home for your dates.</p>
                </div>

                {!showEnquiry ? (
                  <Button onClick={() => setShowEnquiry(true)} className="w-full bg-[#1E3F33] text-white hover:bg-[#163026] rounded-sm py-3 text-base" data-testid="find-alternative-btn">
                    <Send className="w-4 h-4 mr-2" /> Find Me an Alternative
                  </Button>
                ) : (
                  <form onSubmit={submitEnquiry} className="space-y-4" data-testid="enquiry-form">
                    <h3 className="text-lg font-heading font-medium text-[#1F2A25]">Send an Enquiry</h3>
                    <p className="text-sm text-[#6B726A]">We'll check availability with our network and get back to you.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-[#1F2A25]">Full Name *</Label>
                        <Input value={enquiry.name} onChange={e => setEnquiry({...enquiry, name: e.target.value})} required className="mt-1 border-[#E6D5B8]" data-testid="enquiry-name" />
                      </div>
                      <div>
                        <Label className="text-xs text-[#1F2A25]">Email *</Label>
                        <Input type="email" value={enquiry.email} onChange={e => setEnquiry({...enquiry, email: e.target.value})} required className="mt-1 border-[#E6D5B8]" data-testid="enquiry-email" />
                      </div>
                      <div>
                        <Label className="text-xs text-[#1F2A25]">Phone (optional)</Label>
                        <Input value={enquiry.phone} onChange={e => setEnquiry({...enquiry, phone: e.target.value})} className="mt-1 border-[#E6D5B8]" data-testid="enquiry-phone" />
                      </div>
                      <div>
                        <Label className="text-xs text-[#1F2A25]">Guests</Label>
                        <Input type="number" min="1" max="8" defaultValue="4" className="mt-1 border-[#E6D5B8]" data-testid="enquiry-guests" />
                      </div>
                    </div>
                    <div className="bg-[#F0EBE1] p-3 rounded text-sm text-[#1F2A25]">
                      Dates: <strong>{dateRange.from?.toLocaleDateString('en-GB')}</strong> to <strong>{dateRange.to?.toLocaleDateString('en-GB')}</strong>
                    </div>
                    <div>
                      <Label className="text-xs text-[#1F2A25]">Message (optional)</Label>
                      <Textarea value={enquiry.message} onChange={e => setEnquiry({...enquiry, message: e.target.value})} placeholder="Any preferences or requirements..." className="mt-1 border-[#E6D5B8] min-h-[80px]" data-testid="enquiry-message" />
                    </div>
                    <Button type="submit" disabled={enquirySubmitting} className="w-full bg-[#CC5A3D] text-white hover:bg-[#B34A30] rounded-sm py-3" data-testid="submit-enquiry-btn">
                      {enquirySubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      {enquirySubmitting ? 'Sending...' : 'Send Enquiry'}
                    </Button>
                  </form>
                )}
              </div>
            )}

            {/* ENQUIRY SUBMITTED */}
            {enquiryResult === 'success' && (
              <div className="text-center py-6" data-testid="enquiry-success">
                <CheckCircle className="w-12 h-12 text-[#2D6A4F] mx-auto mb-3" />
                <p className="text-lg font-heading font-medium text-[#1F2A25] mb-2">Enquiry Sent!</p>
                <p className="text-sm text-[#6B726A]">Thank you — Sue will be in touch shortly with alternative options for your dates.</p>
              </div>
            )}
            {enquiryResult === 'error' && (
              <div className="text-center py-6" data-testid="enquiry-error">
                <AlertCircle className="w-12 h-12 text-[#BC4749] mx-auto mb-3" />
                <p className="text-lg font-heading font-medium text-[#1F2A25] mb-2">Something went wrong</p>
                <p className="text-sm text-[#6B726A]">Please try again or contact us directly.</p>
                <Button onClick={() => setEnquiryResult(null)} variant="outline" className="mt-3 border-[#E6D5B8]">Try Again</Button>
              </div>
            )}

            {/* No dates selected yet */}
            {!dateRange.from && !dateRange.to && (
              <p className="text-sm text-center text-[#6B726A]">Select your dates above to see prices</p>
            )}
          </div>

          {/* Persistent enquiry fallback — always visible below calendar */}
          <div className="mt-8 bg-[#1E3F33] border border-[#163026] p-8 md:p-10 rounded-xl text-center" data-testid="enquiry-fallback">
            <Send className="w-8 h-8 text-[#CC5A3D] mx-auto mb-4" />
            <h3 className="text-2xl font-heading font-medium text-white mb-3">Can't find your dates?</h3>
            <p className="text-sm text-white/70 mb-2 max-w-lg mx-auto leading-relaxed">
              Don't worry — we can help you find availability or connect you with other trusted caravan owners at Hoburne Park.
            </p>
            <p className="text-xs text-white/50 mb-6">We'll personally help you find the perfect caravan for your stay.</p>
            <Button onClick={() => { scrollTo('availability'); setShowEnquiry(true); setAvailability(false); setEnquiryResult(null); }} className="bg-[#CC5A3D] text-white hover:bg-[#B34A30] rounded-sm px-8 py-3 text-base font-medium" data-testid="cant-find-dates-btn">
              <Send className="w-4 h-4 mr-2" /> Send an Enquiry
            </Button>
          </div>
        </div>
      </section>

      {/* ── 4. PARK INFO ── */}
      <section id="about" className="py-16 px-6 md:px-12 lg:px-24" data-testid="about-section">
        <div className="max-w-[1440px] mx-auto">
          {/* Park teaser */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-8 text-xs text-[#6B726A]">
            <span className="flex items-center gap-1.5"><Waves className="w-3.5 h-3.5 text-[#1E3F33]" /> Indoor & outdoor pools</span>
            <span className="flex items-center gap-1.5"><Music className="w-3.5 h-3.5 text-[#1E3F33]" /> Live entertainment</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#1E3F33]" /> Easy access to beaches & the New Forest</span>
          </div>

          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#CC5A3D] mb-3">The Location</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#1F2A25] mb-6">Hoburne Park Caravan Holidays</h2>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-7">
              <p className="text-base leading-relaxed text-[#6B726A] mb-4">
                Nestled in the picturesque town of Christchurch, Hoburne Park offers the perfect blend of coastal charm and countryside tranquillity. With easy access to sandy beaches, the New Forest National Park, and a wealth of local attractions, this award-winning holiday park is ideal for families.
              </p>
              <p className="text-sm text-[#1E3F33] font-medium italic border-l-2 border-[#CC5A3D] pl-4">
                Private caravan hire at Hoburne Park in the New Forest, Dorset. Ideal for family holidays near Christchurch and Bournemouth.
              </p>
            </div>
            <div className="lg:col-span-5 flex items-start">
              <div className="bg-[#F0EBE1] border border-[#E6D5B8] p-6 rounded-lg w-full">
                <div className="flex items-center gap-2 mb-3"><MapPin className="w-4 h-4 text-[#CC5A3D]" /><span className="text-sm font-medium text-[#1F2A25]">Christchurch, Dorset BH23 4HP</span></div>
                <p className="text-sm text-[#6B726A] mb-1">5 minutes to Avon Beach</p>
                <p className="text-sm text-[#6B726A] mb-1">Adjacent to the New Forest</p>
                <p className="text-sm text-[#6B726A]">20 minutes to Bournemouth</p>
              </div>
            </div>
          </div>
          <h3 className="text-xl font-heading font-medium text-[#1F2A25] mb-6">Park Facilities</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4" data-testid="facilities-grid">
            {FACILITIES.map((f, i) => (
              <div key={i} className="bg-white border border-[#E6D5B8] p-5 rounded-lg text-center transition-transform duration-300 hover:-translate-y-1" data-testid={`facility-${i}`}>
                <div className="w-10 h-10 bg-[#F0EBE1] rounded-lg flex items-center justify-center mx-auto mb-3"><f.icon className="w-5 h-5 text-[#1E3F33]" /></div>
                <span className="text-xs font-medium text-[#1F2A25]">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. PARK GALLERY ── */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#F0EBE1]" data-testid="park-gallery-section">
        <div className="max-w-[1440px] mx-auto">
          <h3 className="text-xl font-heading font-medium text-[#1F2A25] mb-6">Park Gallery</h3>
          <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory" style={{ scrollbarWidth: 'thin' }} data-testid="park-gallery">
            {PARK_GALLERY.map((img, i) => (
              <div key={i} className="snap-start shrink-0 w-[280px] sm:w-[320px] md:w-[360px] rounded-lg overflow-hidden border border-[#E6D5B8] cursor-pointer transition-transform hover:-translate-y-1" onClick={() => openLightbox(PARK_GALLERY.map(p => ({ url: p.url, alt: p.alt, label: p.label })), i)} data-testid={`park-img-${i}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={img.url} alt={img.alt} title={img.label} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" loading="lazy" />
                </div>
                <div className="bg-white px-4 py-2.5">
                  <span className="text-xs font-medium text-[#1E3F33]">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#6B726A] mt-2 text-center">Scroll to see more &middot; Click any image to enlarge</p>
        </div>
      </section>

      {/* ── 6. THINGS TO DO NEARBY ── */}
      <section id="nearby" className="py-16 px-6 md:px-12 lg:px-24" data-testid="nearby-section">
        <div className="max-w-[1440px] mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#CC5A3D] mb-3">Explore The Area</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#1F2A25] mb-3">Things to Do Near Hoburne Park</h2>
          <p className="text-base text-[#6B726A] mb-10 max-w-2xl">Beaches near Christchurch, New Forest attractions, and family days out — all within easy reach.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-testid="nearby-grid">
            {NEARBY.map((item, i) => (
              <div key={i} className="bg-white border border-[#E6D5B8] rounded-lg overflow-hidden transition-transform duration-300 hover:-translate-y-1" data-testid={`nearby-${i}`}>
                <div className="img-zoom-wrapper h-40"><img src={item.img} alt={item.name} className="w-full h-full object-cover" loading="lazy" /></div>
                <div className="p-5">
                  <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#CC5A3D]">{item.cat}</span>
                  <h4 className="text-lg font-heading font-medium text-[#1F2A25] mt-1 mb-1">{item.name}</h4>
                  <p className="text-xs text-[#6B726A] mb-2">{item.desc}</p>
                  <span className="text-xs font-medium text-[#1E3F33]"><MapPin className="w-3 h-3 inline mr-1" />{item.distance}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-lg overflow-hidden border border-[#E6D5B8]" data-testid="map-embed">
            <iframe title="Hoburne Park Location" src="https://maps.google.com/maps?q=Hoburne+Park+Christchurch+Dorset+BH23+4HP&t=&z=13&ie=UTF8&iwloc=&output=embed" width="100%" height="300" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
      </section>

      {/* ── 7. REVIEWS ── */}
      <section id="reviews" className="py-16 px-6 md:px-12 lg:px-24 bg-[#F0EBE1]" data-testid="reviews-section">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-[#CC5A3D]" fill="#CC5A3D" />)}
            </div>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#1F2A25]">{avgRating} &middot; {REVIEWS.length} Reviews</h2>
          </div>
          <p className="text-sm text-[#6B726A] mb-8">Based on verified guest reviews</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-grid">
            {REVIEWS.map((r, i) => (
              <div key={i} className="bg-white border border-[#E6D5B8] p-6 rounded-lg" data-testid={`review-${i}`}>
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-3.5 h-3.5" fill={j < r.rating ? '#CC5A3D' : 'none'} stroke={j < r.rating ? '#CC5A3D' : '#E6D5B8'} />
                  ))}
                </div>
                <p className="text-sm text-[#1F2A25] leading-relaxed mb-4 italic">"{r.text}"</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#1F2A25]">{r.name}</span>
                  <span className="text-xs text-[#6B726A]">{r.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 8. WHY BOOK DIRECT + THINGS TO KNOW ── */}
      <section id="why-direct" className="py-16 px-6 md:px-12 lg:px-24" data-testid="why-direct-section">
        <div className="max-w-[1440px] mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#CC5A3D] mb-3">The Smart Choice</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#1F2A25] mb-10">Why Book Direct With Us</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { icon: CreditCard, title: "No Airbnb Fees", desc: "Save up to 15% compared to Airbnb. Every penny saved is more for your holiday." },
              { icon: MessageCircle, title: "Direct Communication", desc: "Speak directly with Nick and Sue. No middleman, quick answers." },
              { icon: Shield, title: "Best Price Guaranteed", desc: "You won't find this caravan cheaper anywhere else." },
              { icon: MapPin, title: "Local Support", desc: "Sue manages everything locally. Help is always a phone call away." },
            ].map((b, i) => (
              <div key={i} className="bg-white border border-[#E6D5B8] p-6 rounded-lg transition-transform duration-300 hover:-translate-y-1" data-testid={`why-direct-${i}`}>
                <div className="w-10 h-10 bg-[#F0EBE1] rounded-lg flex items-center justify-center mb-4"><b.icon className="w-5 h-5 text-[#1E3F33]" /></div>
                <h3 className="text-lg font-heading font-medium text-[#1F2A25] mb-2">{b.title}</h3>
                <p className="text-sm text-[#6B726A] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>

          {/* Things to Know */}
          <div data-testid="things-to-know-section">
            <h3 className="text-2xl font-heading font-medium text-[#1F2A25] mb-6">Things to Know</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="house-rules">
              {HOUSE_RULES.map((r, i) => (
                <div key={i} className="flex items-center gap-3 bg-white border border-[#E6D5B8] p-4 rounded-lg" data-testid={`rule-${i}`}>
                  <r.icon className="w-5 h-5 text-[#1E3F33] flex-shrink-0" />
                  <span className="text-sm text-[#1F2A25]">{r.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 9. TRUST & FINAL CTA ── */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#1E3F33]" data-testid="trust-section">
        <div className="max-w-3xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 text-center mb-12">
            {[
              { icon: CreditCard, text: "Book direct and save — no Airbnb fees" },
              { icon: CheckCircle, text: "Professionally cleaned and inspected" },
              { icon: MapPin, text: "Managed locally by Sue" },
              { icon: Lock, text: "Secure booking via Stripe" },
            ].map((t, i) => (
              <div key={i} className="flex flex-col items-center gap-3" data-testid={`trust-badge-${i}`}>
                <div className="w-11 h-11 bg-[#CC5A3D] rounded-full flex items-center justify-center"><t.icon className="w-5 h-5 text-white" /></div>
                <p className="text-xs text-[#F9F8F6]/90 leading-relaxed">{t.text}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <p className="text-[#F9F8F6]/70 text-base mb-6 font-heading italic">Hoburne Park caravan hire — New Forest caravan holidays made simple.</p>
            <Button onClick={() => scrollTo('availability')} className="bg-[#CC5A3D] text-white hover:bg-[#B34A30] rounded-sm px-10 py-3.5 text-base font-medium shadow-lg shadow-black/20" data-testid="final-cta-btn">
              Book Your Stay <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
